import { Label } from "@/components/ui/label"
import { FileUploadField } from "@/components/ui/file-upload-field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { VoiceInputTextarea } from "@/components/VoiceInputTextarea"
import { CRUCIAL_CONVERSATION_FIELDS } from "@/lib/crucial-conversation-fields"

export function CrucialConversationForm({
  form,
  existingPdfFileName,
  isCoach = false,
  isEditing = false,
  groupMembers = [],
}) {
  const set = (field) => (value) => form.setData(field, value)

  return (
    <div className="space-y-6">
      <p className="text-xs text-zinc-500">
        Tip: Tap the mic in any field to dictate your answer. Audio is transcribed via our AI provider and not stored.
      </p>

      {isCoach && !isEditing && groupMembers.length > 0 && (
        <div className="space-y-2">
          <Label>Create for member (optional)</Label>
          <Select
            value={form.data.account_id || "self"}
            onValueChange={(v) => form.setData("account_id", v === "self" ? "" : v)}
          >
            <SelectTrigger className="w-full bg-white border-zinc-300">
              <SelectValue placeholder="Yourself" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="self">Myself (coach)</SelectItem>
              {groupMembers.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-zinc-500">
            When set, the conversation is owned by the selected member but recorded as created by you.
          </p>
        </div>
      )}

      {/* Questions render from the shared field constant so the wording and
          order stay in lockstep with the detail and print views. Placeholders
          are intentionally omitted: the label is the single statement of each
          question (old-form placeholders are unknown and paraphrases read as
          "different wording"). */}
      {CRUCIAL_CONVERSATION_FIELDS.map((field) => (
        <VoiceInputTextarea
          key={field.name}
          id={field.name}
          label={field.label}
          required={field.required}
          rows={3}
          value={form.data[field.name]}
          onChange={set(field.name)}
          error={form.errors[field.name]}
        />
      ))}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select
            value={form.data.category}
            onValueChange={(value) => form.setData("category", value)}
          >
            <SelectTrigger className="w-full bg-white border-zinc-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
              <SelectItem value="spiritual">Spiritual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Urgency (1-10)</Label>
          <Select
            value={String(form.data.urgency)}
            onValueChange={(value) => form.setData("urgency", Number(value))}
          >
            <SelectTrigger className="w-full bg-white border-zinc-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pdf_file">Supporting Document (PDF)</Label>
        <FileUploadField
          id="pdf_file"
          accept="application/pdf"
          value={form.data.pdf_file}
          onChange={(f) => form.setData("pdf_file", f)}
          existingFileName={existingPdfFileName}
          helpText="PDF only, max 10MB"
        />
        {form.errors.pdf_file && (
          <p className="text-sm text-red-600">{form.errors.pdf_file}</p>
        )}
      </div>
    </div>
  )
}
